/**
 * Feature switches — the admin-facing view of the runtime switches for product features.
 *
 * One row applies to the whole deployment. A switch is off unless turned on, and when on it can be
 * narrowed to roles, brands, named users, a share of users (rollout) and a date window. The API decides
 * who a switch is on for (FeatureSwitchEvaluator); a change reaches every user within about half a minute.
 */

/** The settings a switch can have — what PUT takes and what the history records. */
export interface FeatureSwitchSettings {
  /** The master kill: false is off for everyone, whatever the targeting says. */
  isEnabled: boolean;
  /** 0–100: the share of otherwise-eligible users who get the feature. */
  rolloutPercent: number;
  /** Role codes (e.g. TCMGR); empty means every role. */
  targetRoles: string[];
  /** LEAP brand ids; empty means every brand. */
  targetBrandIds: number[];
  /** User ids that always get it while it is on and in its window. */
  includeUserIds: number[];
  /** User ids that never get it. Beats includeUserIds. */
  excludeUserIds: number[];
  /** Inclusive start (UTC ISO), or null for open. */
  startUtc: string | null;
  /** Exclusive end (UTC ISO), or null for open. */
  endUtc: string | null;
}

/**
 * Where a switch is in its lifecycle (behind featureswitches.lifecycle). Dark: nobody sees it. RollingOut: its
 * targeting decides. Adopted: everyone who holds the linked role feature; targeting is ignored.
 */
export type FeatureSwitchStage = 'Dark' | 'RollingOut' | 'Adopted';

/** Body of POST api/Maintenance/FeatureSwitches/{switchKey}/stage. */
export interface FeatureSwitchStageRequest {
  stage: FeatureSwitchStage;
}

/** One switch and its current settings. */
export interface FeatureSwitch extends FeatureSwitchSettings {
  switchKey: string;
  displayName: string;
  description: string | null;
  /** The New Features tab it is listed under: Search, Sync, Access or Other. Missing or unknown means Other. */
  category?: string | null;
  /**
   * Sub-switch: the parent's switchKey, or null for a top-level switch. A sub-switch is on only while its
   * parent is. Not part of the update request: the API keeps the current parent when none is sent.
   */
  parentKey?: string | null;
  /** The date it first merged to dev (ISO, date only is meaningful), set by the LeapDb seed. Null if unknown. */
  introducedOn?: string | null;
  /** The role feature (dbo.Feature) the switch gates, or null for a pure behaviour switch. */
  featureId?: number | null;
  /** That feature's name, e.g. EmailHistoryView, or null for a behaviour switch. */
  featureName?: string | null;
  /** Its lifecycle stage. */
  stage?: FeatureSwitchStage;
  /** Role codes holding the linked feature; empty for a behaviour switch. TargetRoles may only narrow to these. */
  grantedRoles?: string[];
  /** Who last changed it, and when — null until someone does. */
  updatedBy: string | null;
  updatedUtc: string | null;
}

/**
 * Body of PUT api/Maintenance/FeatureSwitches/{switchKey}. It REPLACES every setting, so always send
 * the whole object — even for a master toggle.
 */
export type FeatureSwitchUpdateRequest = FeatureSwitchSettings;

/**
 * Body of POST api/FeatureSwitches/TurnOff. It only ever turns features off, so there is no on/off field.
 * Both filters are optional; with neither it covers every feature that is on.
 */
export interface FeatureSwitchTurnOffRequest {
  /** yyyy-MM-dd: only features introduced on or after this date. */
  introducedOnOrAfter?: string;
  /** Only features in this category (a New Features tab). */
  category?: string;
  /** Adopted switches are left on unless this is true (only sent while featureswitches.lifecycle is on). */
  includeAdopted?: boolean;
  /** True lists what would go off and changes nothing. */
  dryRun: boolean;
}

/** One feature in a turn-off result. */
export interface FeatureSwitchTurnOffItem {
  switchKey: string;
  displayName: string;
  introducedOn: string | null;
  category: string | null;
  parentKey: string | null;
  /** Set when the switch was left out, e.g. because it is adopted; such an item does not go off. */
  skippedReason?: string;
}

/** What would go off (dry run) or what went off. */
export interface FeatureSwitchTurnOffResult {
  dryRun: boolean;
  switches: FeatureSwitchTurnOffItem[];
  count: number;
}

/** One row of GET api/Maintenance/FeatureSwitches/{switchKey}/history (latest 50, newest first). */
export interface FeatureSwitchHistoryEntry {
  featureSwitchHistoryId: number;
  switchKey: string;
  changedBy: string | null;
  changedUtc: string;
  before: FeatureSwitchSettings | null;
  after: FeatureSwitchSettings | null;
}

/** The full settings of a switch, ready to send back (lists copied, missing ones empty). */
export function toUpdateRequest(item: FeatureSwitchSettings): FeatureSwitchUpdateRequest {
  return {
    isEnabled: !!item.isEnabled,
    rolloutPercent: item.rolloutPercent == null ? 100 : item.rolloutPercent,
    targetRoles: [...(item.targetRoles || [])],
    targetBrandIds: [...(item.targetBrandIds || [])],
    includeUserIds: [...(item.includeUserIds || [])],
    excludeUserIds: [...(item.excludeUserIds || [])],
    startUtc: item.startUtc || null,
    endUtc: item.endUtc || null
  };
}
